import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Separator } from './ui/separator';
import { BookOpen, Mail, Heart } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Start Here', href: '/start' },
    { name: 'Store', href: '/store' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'Start a Project', href: '/start-project' },
  ];

  const brands = [
    { name: 'Power Moves', href: '/powermoves' },
    { name: 'Mindset Shift', href: '/mindsetshift' },
    { name: 'THIS or THAT', href: '/thisorthat' },
    { name: 'Author eCoSystem', href: '/authors' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/67pzcjdk_eBlackBookListings.png" 
                alt="eBlackBookListings"
                className="h-8 w-auto"
              />
              <span className="text-lg font-bold">eBlackBookListings</span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Empowering voices through compelling narratives and platforms that amplify 
              underrepresented stories in literature and business.
            </p>
            <div className="flex items-center space-x-2 text-red-600">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium">Author • Visionary • Creator</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link.href)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Brands</h3>
            <ul className="space-y-3">
              {brands.map((brand, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(brand.href)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 text-left"
                  >
                    {brand.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Connect</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-red-600" />
                <a 
                  href="mailto:info@eBlackBookListings.com"
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                >
                  info@eBlackBookListings.com
                </a>
              </div>
              <div className="text-gray-400 text-sm">
                <p>Available for projects worldwide</p>
                <p className="mt-2">Response time: Within 24 hours</p>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg">
              <h4 className="text-sm font-semibold mb-2">Stay Updated</h4>
              <p className="text-xs text-gray-400 mb-3">
                Get notified about new book releases and writing tips.
              </p>
              <button
                onClick={() => navigate('/start')}
                className="w-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-gray-800" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm text-gray-400">
            <p>© {currentYear} Audrey Egypt Young. All rights reserved.</p>
            <p className="mt-1">Powered by eBlackBookListings</p>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <span>Made with</span>
            <Heart className="w-4 h-4 text-red-600 fill-current" />
            <span>for authors and creators everywhere</span>
          </div>

          <div className="flex space-x-6 text-sm text-gray-400">
            <button 
              onClick={() => navigate('/privacy-policy')}
              className="hover:text-red-500 transition-colors duration-200"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate('/terms-of-service')}
              className="hover:text-red-500 transition-colors duration-200"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;